import React, { Children } from 'react';
import classNames from 'classnames';

import { Icon } from 'js/design_system/Icon';
import { ButtonProps } from '../Button';
import { trackClick } from 'js/utils/trackClick';

import 'app/assets/stylesheets/design_system/button.scss';

type ButtonLinkProps = {
  external?: boolean;
} & ButtonProps &
  React.ComponentPropsWithoutRef<'a'>;

/**
 * A button for navigation links to other pages. Supports several variants for different contexts,
 * as well as the `href` prop for specifying the target URL and other native anchor attributes.
 * Also supports external links, which will automatically have an icon and open in a new window.
 *
 * Props:
 * - `external`: Whether the link should act as an external link (default: false)
 * - `size`: The size of the button - one of 'xs', 'sm', or 'lg' (default: 'lg')
 * - `secondary`: Button for secondary actions, usually paired with a primary. Not valid for 'xs' size
 *     buttons. (default: false)
 * - `inverse`: Inverse-color button for dark backgrounds - NOT the same as secondary and can be combined
 *     with it. Not valid for 'xs' size buttons. (default: false)
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to render as
 *     the button's icon (left of the text)
 * - `iconRight`: The name of a Material Symbols icon (string) or an arbitrary React component to render
 *     as the button's icon (right of the text)
 * - `fullWidth`: Full width button that takes up the entire width of its container (default: false)
 * - `trackClick`: Options for tracking clicks on this button link
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { ButtonLink } from 'js/design_system/link/ButtonLink';
 *
 * <ButtonLink href="/about" secondary>
 *   Click me!
 * </ButtonLink>
 * ```
 *
 */
export const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    {
      secondary = false,
      size = 'lg',
      inverse = false,
      external = false,
      icon,
      iconRight,
      fullWidth = false,
      className,
      children,
      trackClick: trackClickOptions,
      onClick,
      rel: relProp,
      ...restProps
    },
    ref,
  ) => {
    const classes = classNames('ds2-5-button', className, {
      'ds2-5-button--small': size === 'sm',
      'ds2-5-button--extra-small': size === 'xs',
      'ds2-5-button--secondary': secondary,
      'ds2-5-button--inverse': inverse,
      'ds2-5-button--full-width': fullWidth,
      'ds2-5-button--icon-only': Children.count(children) === 0,
    });

    let iconView = null;
    if (icon) {
      iconView =
        typeof icon === 'string' ? (
          <Icon name={icon} size={size === 'xs' ? 16 : 20} />
        ) : (
          icon
        );
    }

    let iconRightView = null;
    if (iconRight) {
      iconRightView =
        typeof iconRight === 'string' ? (
          <Icon name={iconRight} size={size === 'xs' ? 16 : 20} />
        ) : (
          iconRight
        );
    } else if (external) {
      iconRightView = (
        <Icon name="arrow_outward" size={size === 'xs' ? 16 : 20} />
      );
    }

    const rel = [relProp, external ? 'noreferrer' : ''].filter(Boolean);

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return onClick?.(event);
        }
      : onClick;

    return (
      // This IS the design system implementation:
      // eslint-disable-next-line no-restricted-syntax
      <a
        className={classes}
        target={external ? '_blank' : undefined}
        {...restProps}
        rel={rel.length > 0 ? rel.join(' ') : undefined}
        onClick={handleClick}
        ref={ref}
      >
        {iconView ? iconView : null}
        {children}
        {iconRightView ? iconRightView : null}
      </a>
    );
  },
);
